/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import { PAGINATED_LIST_REQUEST } from "../utils/constants.js";

// FIX default import https://github.com/microsoft/TypeScript/issues/49189
import {
  AbsoluteTime,
  AccessToken,
  ListOrdersRequestParams,
  opFixedSuccess,
  TalerError,
  TalerHttpError,
  TalerMerchantManagementResultByMethod,
} from "@gnu-taler/taler-util";
import {
  buildPaginatedResult,
  LONG_POLL_DELAY,
  useLongPolling,
} from "@gnu-taler/web-util/browser";
import _useSWR, { mutate, SWRHook } from "swr";
import { useSessionContext } from "../context/session.js";

const useSWR = _useSWR as unknown as SWRHook;

export function revalidateOrderDetails() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getOrderDetails",
    undefined,
    { revalidate: true },
  );
}
export function useOrderDetails(oderId: string) {
  const { state, lib } = useSessionContext();

  async function fetcher([dId, token]: [string, AccessToken]) {
    return await lib.instance.getOrderDetails(token, dId);
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"getOrderDetails">,
    TalerHttpError
  >([oderId, state.token, "getOrderDetails"], fetcher);

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function useOrderDetailsWithLongPoll(orderId: string) {
  const { state, lib } = useSessionContext();
  const token = state.token;

  async function fetcher([dId, token]: [string, AccessToken]) {
    return await lib.instance.getOrderDetails(token, dId);
  }

  const { data, error, mutate } = useSWR<
    TalerMerchantManagementResultByMethod<"getOrderDetails">,
    TalerHttpError
  >([orderId, token, "getOrderDetails"], fetcher);

  const result = useLongPolling(
    data,
    (result) => {
      return result !== undefined && result.type === "ok";
    },
    async (ct, latestStatus) => {
      if (latestStatus === undefined || latestStatus.type === "fail")
        return undefined;
      const r = await lib.instance.getOrderDetails(token!, orderId, {
        longpoll: {
          etag: latestStatus.body.etag!,
          timeout: LONG_POLL_DELAY,
        },
        ct,
      });
      await mutate(r, { revalidate: false });
      return r;
    },
    [orderId],
    { minTime: LONG_POLL_DELAY },
  );

  if (error) return error;
  return result;
}

export interface InstanceOrderFilter {
  paid?: boolean;
  refunded?: boolean;
  wired?: boolean;
  date?: AbsoluteTime;
  position?: string;
}

export function revalidateInstanceOrders() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "listOrders",
    undefined,
    { revalidate: true },
  );
}
export function useInstanceOrders(
  args?: InstanceOrderFilter,
  updatePosition: (d: string | undefined) => void = () => {},
) {
  const { state, lib } = useSessionContext();
  const token = state.token!;

  const cacheKey = [
    args?.position,
    args?.paid,
    args?.refunded,
    args?.wired,
    args?.date,
    "listOrders",
  ];

  async function fetcher([position, paid, refunded, wired, date]: any) {
    return await lib.instance.listOrders(token, {
      limit: PAGINATED_LIST_REQUEST,
      order: "dec",
      offset: position,
      paid: paid,
      refunded: refunded,
      wired: wired,
      date: date,
    });
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"listOrders">,
    TalerHttpError
  >(cacheKey, fetcher);

  const result = useLongPolling(
    data,
    (result) => {
      return result !== undefined && result.type === "ok";
    },
    async (ct, latestStatus, args) => {
      if (latestStatus === undefined || latestStatus.type === "fail")
        return undefined;

      const params: ListOrdersRequestParams = {
        limit: 1,
        order: "asc",
        offset: args[0],
        paid: args[1],
        refunded: args[2],
        wired: args[3],
        date: args[4],
      };
      const result = await lib.instance.listOrders(token, {
        ...params,
        timeout: LONG_POLL_DELAY,
        ct,
      });
      const merged =
        result.type === "fail" || result.body.orders.length === 0
          ? latestStatus.body.orders
          : [...result.body.orders, ...latestStatus.body.orders];

      const newResult = opFixedSuccess({ orders: merged });
      mutate(newResult, { revalidate: false });
      return newResult;
    },
    cacheKey,
    { minTime: LONG_POLL_DELAY },
  );

  if (error || result instanceof TalerError) return error;
  if (result === undefined) return undefined;
  if (result.type !== "ok") return result;

  return buildPaginatedResult(
    result.body.orders,
    args?.position,
    updatePosition,
    (d) => String(d.row_id),
    PAGINATED_LIST_REQUEST,
  );
}
